import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, LineChart as LineChartIcon, Store, Phone, MapPinned, MousePointerClick,
} from "lucide-react";

const PERIODS = [
  { value: 7, label: "7 días" },
  { value: 28, label: "28 días" },
  { value: 90, label: "90 días" },
];

const GBP_STATUS_BADGE = {
  ready: { label: "Ficha conectada", cls: "bg-[#10B981]/10 text-[#065F46]" },
  pending_approval: { label: "Esperando la aprobación de Google", cls: "bg-[#F59E0B]/10 text-[#92400E]" },
  google_not_connected: { label: "Falta conectar Google", cls: "bg-[#F59E0B]/10 text-[#92400E]" },
  unavailable: { label: "Pendiente de habilitar", cls: "bg-[#F4F4F5] text-[#71717A]" },
  error: { label: "Error al verificar", cls: "bg-[#EF4444]/10 text-[#991B1B]" },
};

function formatDuration(seconds) {
  const s = Math.round(seconds || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

function minutesAgoLabel(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins === 0) return "Datos actualizados hace instantes";
  if (mins === 1) return "Datos actualizados hace 1 minuto";
  return `Datos actualizados hace ${mins} minutos`;
}

function VariationBadge({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${up ? "text-[#10B981]" : "text-[#EF4444]"}`}>
      <Icon className="h-3 w-3" /> {up ? "+" : ""}{pct}%
    </span>
  );
}

function MetricCard({ label, value, variationPct, testid }) {
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid={testid}>
      <span className="text-xs uppercase tracking-wider text-[#71717A] font-medium">{label}</span>
      <div className="mt-3 flex items-end justify-between">
        <span className="font-heading text-3xl font-extrabold text-[#09090B]">{value}</span>
        <VariationBadge pct={variationPct} />
      </div>
    </Card>
  );
}

function GbpTrafficCard({ gbpStatus, gbpMetrics, gbpMetricsLoading }) {
  const status = gbpStatus?.gbp_access_status || "unavailable";
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="gbp-traffic-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-heading font-bold text-sm text-[#09090B] flex items-center gap-2">
          <Store className="h-4 w-4 text-[#0055FF]" /> Google Business Profile
        </h3>
        <Badge
          className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${GBP_STATUS_BADGE[status]?.cls || GBP_STATUS_BADGE.unavailable.cls}`}
          data-testid="gbp-traffic-status-badge"
        >
          {GBP_STATUS_BADGE[status]?.label || GBP_STATUS_BADGE.unavailable.label}
        </Badge>
      </div>
      {status !== "ready" ? (
        <p className="text-xs text-[#71717A]" data-testid="gbp-traffic-pending">
          {gbpStatus?.gbp_access_detail || "Todavía no hay acceso a las métricas de la ficha. Configurala desde Conexiones."}
        </p>
      ) : gbpMetricsLoading || !gbpMetrics ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /></div>
      ) : !gbpMetrics.available ? (
        <p className="text-xs text-[#71717A]">Todavía no hay datos de la ficha para este período.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-2 text-sm" data-testid="gbp-metric-call-clicks">
              <Phone className="h-4 w-4 text-[#0055FF]" />
              <div><div className="font-heading font-bold text-[#09090B]">{gbpMetrics.call_clicks}</div><div className="text-[10px] text-[#71717A]">Toques en "Llamar"</div></div>
            </div>
            <div className="flex items-center gap-2 text-sm" data-testid="gbp-metric-direction-requests">
              <MapPinned className="h-4 w-4 text-[#0055FF]" />
              <div><div className="font-heading font-bold text-[#09090B]">{gbpMetrics.direction_requests}</div><div className="text-[10px] text-[#71717A]">Pidieron cómo llegar</div></div>
            </div>
            <div className="flex items-center gap-2 text-sm" data-testid="gbp-metric-website-clicks">
              <MousePointerClick className="h-4 w-4 text-[#0055FF]" />
              <div><div className="font-heading font-bold text-[#09090B]">{gbpMetrics.website_clicks}</div><div className="text-[10px] text-[#71717A]">Fueron al sitio</div></div>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2">
              Impresiones (4 métricas separadas -- no existe una sola de "búsquedas")
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="bg-[#F4F4F5] rounded-sm p-2" data-testid="gbp-metric-impressions-desktop-maps">Maps escritorio: <b>{gbpMetrics.impressions_desktop_maps}</b></div>
              <div className="bg-[#F4F4F5] rounded-sm p-2" data-testid="gbp-metric-impressions-desktop-search">Búsqueda escritorio: <b>{gbpMetrics.impressions_desktop_search}</b></div>
              <div className="bg-[#F4F4F5] rounded-sm p-2" data-testid="gbp-metric-impressions-mobile-maps">Maps mobile: <b>{gbpMetrics.impressions_mobile_maps}</b></div>
              <div className="bg-[#F4F4F5] rounded-sm p-2" data-testid="gbp-metric-impressions-mobile-search">Búsqueda mobile: <b>{gbpMetrics.impressions_mobile_search}</b></div>
            </div>
          </div>
          {gbpMetrics.search_keywords?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2">Palabras clave del mes</p>
              <div className="flex flex-wrap gap-1.5" data-testid="gbp-search-keywords">
                {gbpMetrics.search_keywords.slice(0, 15).map((k, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 rounded-sm bg-[#F4F4F5] text-[#3F3F46]">{k.keyword}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function AnalyticsPage() {
  const { activeTenantId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [googleConnected, setGoogleConnected] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [days, setDays] = useState(28);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectingProperty, setSelectingProperty] = useState(false);
  const [gbpStatus, setGbpStatus] = useState(null);
  const [gbpMetrics, setGbpMetrics] = useState(null);
  const [gbpMetricsLoading, setGbpMetricsLoading] = useState(false);

  const loadProperties = useCallback(async () => {
    setLoading(true);
    try {
      const statusRes = await api.get("/google/status");
      if (!statusRes.data || statusRes.data.connection_status !== "connected") {
        setGoogleConnected(false);
        return;
      }
      setGoogleConnected(true);
      setSelectedProperty(statusRes.data.selected_ga4_property || null);
      const propsRes = await api.get("/google/analytics/properties");
      setProperties(propsRes.data);
    } catch {
      setGoogleConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) loadProperties(); }, [activeTenantId, loadProperties]);

  const loadGbpStatus = useCallback(async () => {
    try {
      const res = await api.get("/google/business/status");
      setGbpStatus(res.data);
    } catch {
      setGbpStatus(null);
    }
  }, []);

  useEffect(() => { if (activeTenantId) loadGbpStatus(); }, [activeTenantId, loadGbpStatus]);

  useEffect(() => {
    if (gbpStatus?.gbp_access_status !== "ready") return;
    setGbpMetricsLoading(true);
    api.get("/google/business/metrics", { params: { days } })
      .then((r) => setGbpMetrics(r.data))
      .catch(() => setGbpMetrics(null))
      .finally(() => setGbpMetricsLoading(false));
  }, [gbpStatus?.gbp_access_status, days]);

  const selectProperty = useCallback(async (propertyId) => {
    setSelectingProperty(true);
    try {
      await api.post("/google/analytics/select-property", { property: propertyId });
      setSelectedProperty(propertyId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo elegir la propiedad");
    } finally {
      setSelectingProperty(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedProperty && properties.length === 1) selectProperty(properties[0].property);
  }, [properties, selectedProperty, selectProperty]);

  const loadOverview = useCallback(async (refresh = false) => {
    if (!selectedProperty) return;
    if (refresh) setRefreshing(true); else setOverviewLoading(true);
    try {
      const res = await api.get("/google/analytics/overview", { params: { days, refresh } });
      setOverview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo traer los datos de tráfico");
    } finally {
      setOverviewLoading(false);
      setRefreshing(false);
    }
  }, [selectedProperty, days]);

  useEffect(() => { if (selectedProperty) loadOverview(false); }, [selectedProperty, days, loadOverview]);

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" />
      </div>
    );
  }

  const hasData = overview && (
    overview.summary?.sessions?.valor_actual > 0
    || overview.channels?.length > 0
    || overview.daily?.some((d) => d.sessions > 0)
  );

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {!googleConnected ? (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-10 text-center" data-testid="analytics-no-connection">
          <LineChartIcon className="h-8 w-8 text-[#A1A1AA] mx-auto" />
          <p className="mt-3 text-sm text-[#71717A]">No hay una conexión de Google activa.</p>
          <Button
            className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
            onClick={() => navigate("/dashboard/connections")}
            data-testid="analytics-go-to-connections"
          >
            Ir a Conexiones
          </Button>
        </Card>
      ) : properties.length === 0 ? (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-10 text-center" data-testid="analytics-no-properties">
          <LineChartIcon className="h-8 w-8 text-[#A1A1AA] mx-auto" />
          <p className="mt-3 text-sm text-[#71717A]">Esta cuenta de Google no tiene ninguna propiedad de Analytics.</p>
          <p className="mt-1 text-xs text-[#A1A1AA]">Para ver tráfico acá, primero hay que instalar Google Analytics en el sitio.</p>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {properties.length > 1 && (
                <Select value={selectedProperty || undefined} onValueChange={selectProperty} disabled={selectingProperty}>
                  <SelectTrigger data-testid="analytics-property-select" className="h-9 w-64 text-sm">
                    <SelectValue placeholder="Elegí una propiedad" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.property} value={p.property}>{p.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger data-testid="analytics-period-select" className="h-9 w-32 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIODS.map((p) => (
                    <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline" size="sm" onClick={() => loadOverview(true)} disabled={refreshing || !selectedProperty}
              data-testid="analytics-refresh-button" className="rounded-md"
            >
              {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Actualizar
            </Button>
          </div>

          {!selectedProperty ? (
            <Card className="rounded-sm border-[#E4E4E7] shadow-none p-10 text-center" data-testid="analytics-select-property-prompt">
              <p className="text-sm text-[#71717A]">Elegí una propiedad de Analytics para ver el tráfico.</p>
            </Card>
          ) : overviewLoading || !overview ? (
            <div className="flex justify-center py-16" data-testid="analytics-overview-loading">
              <Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" />
            </div>
          ) : !hasData ? (
            <Card className="rounded-sm border-[#E4E4E7] shadow-none p-10 text-center" data-testid="analytics-no-data">
              <p className="text-sm text-[#71717A]">Todavía no hay datos de tráfico para esta propiedad.</p>
            </Card>
          ) : (
            <>
              {overview.stale && (
                <div
                  className="flex items-center gap-2 text-xs text-[#92400E] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-3"
                  data-testid="analytics-stale-banner"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0" /> Se alcanzó el límite diario de consultas a Google. Mostrando el último dato disponible, no es de ahora.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <MetricCard label="Sesiones" value={Math.round(overview.summary.sessions.valor_actual)} variationPct={overview.summary.sessions.variacion_pct} testid="metric-sessions" />
                <MetricCard label="Usuarios" value={Math.round(overview.summary.users.valor_actual)} variationPct={overview.summary.users.variacion_pct} testid="metric-users" />
                <MetricCard label="Páginas vistas" value={Math.round(overview.summary.page_views.valor_actual)} variationPct={overview.summary.page_views.variacion_pct} testid="metric-page-views" />
                <MetricCard label="Duración media" value={formatDuration(overview.summary.avg_duration.valor_actual)} variationPct={overview.summary.avg_duration.variacion_pct} testid="metric-avg-duration" />
                <MetricCard label="Rebote" value={`${(overview.summary.bounce_rate.valor_actual * 100).toFixed(1)}%`} variationPct={overview.summary.bounce_rate.variacion_pct} testid="metric-bounce-rate" />
                {overview.summary.conversions && (
                  <MetricCard label="Conversiones" value={Math.round(overview.summary.conversions.valor_actual)} variationPct={overview.summary.conversions.variacion_pct} testid="metric-conversions" />
                )}
              </div>

              <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="analytics-daily-chart">
                <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Sesiones por día</h3>
                {overview.daily.length === 0 ? (
                  <p className="text-sm text-[#A1A1AA]">Sin datos en este período.</p>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={overview.daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Area type="monotone" dataKey="sessions" stroke="#0055FF" fill="#0055FF" fillOpacity={0.12} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="analytics-channels-card">
                  <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">De dónde viene la gente</h3>
                  {overview.channels.length === 0 ? (
                    <p className="text-sm text-[#A1A1AA]">Sin datos en este período.</p>
                  ) : (
                    <div className="space-y-3">
                      {overview.channels.map((c, idx) => (
                        <div key={c.label} data-testid={`channel-row-${idx}`}>
                          <div className="flex items-center justify-between text-xs text-[#71717A] mb-1">
                            <span>{c.label}</span>
                            <span>{c.sessions} · {c.pct}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-[#F4F4F5] overflow-hidden">
                            <div className="h-full bg-[#0055FF] rounded-full" style={{ width: `${c.pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="analytics-landing-pages-card">
                  <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Páginas por las que entran</h3>
                  {overview.landing_pages.length === 0 ? (
                    <p className="text-sm text-[#A1A1AA]">Sin datos en este período.</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-[1fr_auto_auto] gap-3 text-[10px] uppercase tracking-wider text-[#A1A1AA] pb-1 border-b border-[#F4F4F5]">
                        <span>Página</span><span>Sesiones</span><span>Rebote</span>
                      </div>
                      {overview.landing_pages.map((p, idx) => (
                        <div key={p.page} className="grid grid-cols-[1fr_auto_auto] gap-3 text-sm border-b border-[#F4F4F5] pb-2 last:border-0" data-testid={`landing-page-row-${idx}`}>
                          <span className="truncate text-[#09090B]">{p.page}</span>
                          <span className="text-[#71717A]">{p.sessions}</span>
                          <span className="text-[#71717A]">{(p.bounce_rate * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              <p className="text-[10px] text-[#A1A1AA]" data-testid="analytics-cached-at">
                {minutesAgoLabel(overview.cached_at)}
              </p>
            </>
          )}
        </>
      )}

      <GbpTrafficCard gbpStatus={gbpStatus} gbpMetrics={gbpMetrics} gbpMetricsLoading={gbpMetricsLoading} />
    </div>
  );
}
